import { ApiResponseError, type ApiClient } from "@shell/auth/apiClient";
import { exportListResponseSchema } from "../common/schemas";
import {
    assetAssignmentListResponseSchema,
    assetAssignmentMutationResponseSchema,
    assetAssignmentViewSchema,
    type AssetAssignmentCreatePayload,
    type AssetAssignmentView,
} from "./types";

interface SearchParams {
    readonly assetId?: string;
    readonly userId?: string;
    readonly workspaceId?: string;
    readonly targetKind?: string;
    readonly open?: boolean;
}

export async function searchAssetAssignments(
    client: ApiClient,
    params: SearchParams,
): Promise<readonly AssetAssignmentView[]> {
    const search = new URLSearchParams();
    if (params.assetId !== undefined && params.assetId.length > 0)
        search.set("assetId", params.assetId);
    if (params.userId !== undefined && params.userId.length > 0)
        search.set("userId", params.userId);
    if (params.workspaceId !== undefined && params.workspaceId.length > 0)
        search.set("workspaceId", params.workspaceId);
    if (params.targetKind !== undefined && params.targetKind.length > 0)
        search.set("targetKind", params.targetKind);
    if (params.open !== undefined) search.set("open", params.open ? "true" : "false");

    const path =
        search.size > 0 ? `/api/asset-assignments?${search.toString()}` : "/api/asset-assignments";
    const raw = await client.getJson<unknown>(path);
    return assetAssignmentListResponseSchema.parse(raw).results;
}

export async function exportAssetAssignments(
    client: ApiClient,
    params: SearchParams,
): Promise<{ readonly results: readonly AssetAssignmentView[]; readonly truncated: boolean }> {
    const search = new URLSearchParams();
    if (params.assetId !== undefined && params.assetId.length > 0)
        search.set("assetId", params.assetId);
    if (params.userId !== undefined && params.userId.length > 0)
        search.set("userId", params.userId);
    if (params.workspaceId !== undefined && params.workspaceId.length > 0)
        search.set("workspaceId", params.workspaceId);
    if (params.targetKind !== undefined && params.targetKind.length > 0)
        search.set("targetKind", params.targetKind);
    if (params.open !== undefined) search.set("open", params.open ? "true" : "false");

    const path =
        search.size > 0
            ? `/api/exports/asset-assignments?${search.toString()}`
            : "/api/exports/asset-assignments";
    const raw = await client.getJson<unknown>(path);
    return exportListResponseSchema(assetAssignmentViewSchema).parse(raw);
}

export async function createAssetAssignment(
    client: ApiClient,
    payload: AssetAssignmentCreatePayload,
): Promise<AssetAssignmentView> {
    const body: Record<string, unknown> = {
        id: payload.id,
        assetId: payload.assetId,
        targetKind: payload.targetKind,
    };
    if (payload.userId !== undefined && payload.userId !== null) body["userId"] = payload.userId;
    if (payload.workspaceId !== undefined && payload.workspaceId !== null)
        body["workspaceId"] = payload.workspaceId;

    const response = await client.fetch("/api/asset-assignments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    return await readMutationResponse(response);
}

export async function releaseAssetAssignment(
    client: ApiClient,
    assetId: string,
    assignmentId: string,
): Promise<AssetAssignmentView> {
    const response = await client.fetch(
        `/api/assets/${encodeURIComponent(assetId)}/assignments/${encodeURIComponent(assignmentId)}/release`,
        { method: "PATCH" },
    );
    return await readMutationResponse(response);
}

async function readMutationResponse(response: Response): Promise<AssetAssignmentView> {
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return assetAssignmentMutationResponseSchema.parse(raw).assignment;
}
